import { MessageBarType } from '@fluentui/react';
import React, { createContext, useCallback, useMemo, useReducer } from 'react';
import { Immocom } from '../specifique/immocom';

const NotificationsContext = createContext();
const NotificationsManagerContext = createContext();

export const NotificationsAction = {
    caution: "caution",
    clear: "clear",
    confirm: "confirm",
    dismiss: "dismiss",
    inform: "inform",
    load: "load",
    warn: "warn",
};

export const NotificationsTarget = {
    all: 1,
    main: 2,
    panels: 3
};

export const NotificationContextProvider = ({ children }) => {

    // Créer une notification
    const addNotification = useCallback((notifications, id, type, message, target, code) => {
        if (!id) id = Math.random().toString(16).slice(2);
        if (!target) target = NotificationsTarget.all;
        return [...notifications, { code, id, message, target, type }];
    }, []);

    // Supprimer toutes les notifications (sauf les messages de chargements)
    const clearNotifications = useCallback((notifications) => {
        return notifications.filter(notification => notification.type === -1);
    }, []);

    // Supprimer une notification à l'aide de son ID
    const dismissNotification = useCallback((notifications, id) => {
        return notifications.filter(notification => notification.id !== id);
    }, []);

    const reducer = useCallback((notifications, action) => {
        if (!!action) {
            const { code, id, message, target, type } = action;
            switch (type) {
                case NotificationsAction.caution: return addNotification(notifications, id, MessageBarType.error, message, target, code);
                case NotificationsAction.clear: return clearNotifications(notifications);
                case NotificationsAction.confirm: return addNotification(notifications, id, MessageBarType.success, message, target);
                case NotificationsAction.dismiss: return dismissNotification(notifications, id);
                case NotificationsAction.inform: return addNotification(notifications, id, MessageBarType.info, message, target);
                case NotificationsAction.load: return addNotification(notifications, id, -1, message);
                case NotificationsAction.warn: return addNotification(notifications, id, MessageBarType.warning, message, target);
                default: throw new Error(`Action non reconnue : ${action.type}`)
            }
        }
    }, [addNotification, clear, dismissNotification]);

    // Exemple : [
    //    { id: 1, message: "Chargement...", type: MessageBarType.info },
    //    { id: 2, message: "Une erreur est survenue...", target: NotificationsTarget.panels, type: MessageBarType.error, code: 100 }
    // ]
    const [notifications, dispatch] = useReducer(reducer, []);

    const errorMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.error),
        [notifications]
    );

    const infoMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.info),
        [notifications]
    );

    const loadingMessages = useMemo(
        () => notifications.filter(notification => notification.type === -1),
        [notifications]
    );

    const successMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.success),
        [notifications]
    );

    const warningMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.warning),
        [notifications]
    );

    // Erreur
    const caution = useCallback((message, target, code) => {
        if (!message) message = "Une erreur est survenue";
        dispatch({ type: NotificationsAction.caution, message, target, code });
    }, []);

    // Réussite (bandeau vert, rarement utilisé)
    const confirm = useCallback((message, target) => {
        dispatch({ type: NotificationsAction.confirm, message, target });
    }, []);

    // Information (bandeau gris, rarement utilisé)
    const inform = useCallback((message, target) => {
        dispatch({ type: NotificationsAction.inform, message, target });
    }, []);

    // ProgressIndicator
    const load = useCallback((message) => {
        const id = Math.random().toString(16).slice(2);
        dispatch({ type: NotificationsAction.load, id, message });
        return id;
    }, []);

    // Warning
    const warn = useCallback((message, target, code) => {
        if (!message) message = "Une erreur est survenue";
        dispatch({ type: NotificationsAction.warn, message, target, code });
    }, []);

    const clear = useCallback(() => {
        dispatch({ type: NotificationsAction.clear });
    }, []);

    const dismiss = useCallback((id) => {
        dispatch({ type: NotificationsAction.dismiss, id });
    }, []);

    // Extraire les messages d'un retour d'un appel à une API
    const extract = useCallback((response, target) => {
        const { codeRetour } = response;
        if (Immocom.estCodeErreurUtilisateurNonAuthentifie(codeRetour)) Immocom.deconnexionEtRedirectionVersLogin();
        else {
            const { errorMessage, infoMessage, successMessage, warningMessage } = response;
            if (!!errorMessage) caution(errorMessage, target, codeRetour);
            if (!!successMessage) confirm(successMessage, target);
            if (!!infoMessage) inform(infoMessage, target);
            if (!!warningMessage) warn(warningMessage, target, codeRetour);
        }
    }, [caution, confirm, inform, warn]);

    const value = useMemo(
        () => ({ errorMessages, infoMessages, loadingMessages, successMessages, warningMessages }),
        [errorMessages, infoMessages, loadingMessages, successMessages, warningMessages]
    );

    const managerValue = useMemo(
        () => ({ alert, clear, confirm, dismiss, extract, inform, load, warn }),
        [alert, clear, confirm, dismiss, extract, inform, load, warn]
    );

    return <NotificationsManagerContext.Provider value={managerValue}>
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    </NotificationsManagerContext.Provider>;

};

export const useNotifications = () => {
    const data = React.useContext(NotificationsContext);
    if (data === undefined) throw new Error();
    const { errorMessages, infoMessages, loadingMessages, successMessages, warningMessages } = data;
    return { errorMessages, infoMessages, loadingMessages, successMessages, warningMessages };
};

export const useNotificationsManager = () => {
    const manager = React.useContext(NotificationsManagerContext);
    if (manager === undefined) throw new Error();
    const { alert, clear, confirm, dismiss, extract, inform, load, warn } = manager;
    return { alert, clear, confirm, dismiss, extract, inform, load, warn };
};